import { DollarSign, Calendar, TrendingUp } from 'lucide-react';
import { KPICard } from '../KPICard';
import { AgingTable } from '../AgingTable';
import { AgingDonutChart } from '../AgingDonutChart';
import { CashFlowChart, ChartDataPoint } from '../CashFlowChart';
import { ReceivingRateTable } from '../ReceivingRateTable';
import { AgingPeriod } from '@/data/dashboardMockData';
import { cn } from '@/lib/utils';

interface ResumoTabProps {
  kpiData: {
    receberHoje: number;
    receberHojeClientes: number;
    vencido2d: number;
    vencido2dClientes: number;
    saldoGeral: number;
    saldoGeralFaturas: number;
  };
  chartData: ChartDataPoint[];
  agingData: AgingPeriod[];
  timeFilter: 'day' | 'week' | 'month';
  onTimeFilterChange: (filter: 'day' | 'week' | 'month') => void;
}

const timeFilterLabels = {
  day: 'Dia',
  week: 'Semana',
  month: 'Mês',
};

const timeFilterDescriptions = {
  day: 'Visão Diária - Operacional',
  week: 'Visão Semanal - Tática',
  month: 'Visão Mensal - Estratégica',
};

function formatMoney(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function ResumoTab({ kpiData, chartData, agingData, timeFilter, onTimeFilterChange }: ResumoTabProps) {
  return (
    <div className="space-y-6">
      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard
          title="A Receber Hoje"
          value={formatMoney(kpiData.receberHoje)}
          variant="basic"
          subtitle={`${kpiData.receberHojeClientes} clientes`}
          icon={<Calendar className="h-4 w-4 text-[hsl(var(--basic-600))]" />}
        />
        <KPICard
          title="Vencido há +2 dias"
          value={formatMoney(kpiData.vencido2d)}
          variant="careful"
          subtitle={`${kpiData.vencido2dClientes} clientes em atraso`}
          icon={<TrendingUp className="h-4 w-4 text-[hsl(var(--careful-600))]" />}
        />
        <KPICard
          title="Saldo Geral em Aberto"
          value={formatMoney(kpiData.saldoGeral)}
          variant="accent"
          subtitle={`${kpiData.saldoGeralFaturas} faturas • Visão atual`}
          icon={<DollarSign className="h-4 w-4 text-[hsl(var(--accent-600))]" />}
        />
      </div>

      {/* Tabela Aging + Gráfico Donut lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AgingTable type="open" data={agingData} />
        <AgingDonutChart />
      </div>

      {/* Gráfico de Evolução + Taxa de Recebimento */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-5 rounded-2xl border bg-card hover:shadow-lg transition-all duration-300">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Cobranças Previstas vs Recebidas</h3>
              <p className="text-xs text-muted-foreground">{timeFilterDescriptions[timeFilter]}</p>
            </div>
            
            <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
              {(['day', 'week', 'month'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => onTimeFilterChange(filter)}
                  className={cn(
                    'px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200',
                    timeFilter === filter
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {timeFilterLabels[filter]}
                </button>
              ))}
            </div>
          </div>
          
          <CashFlowChart data={chartData} />
        </div>

        <ReceivingRateTable />
      </div>
    </div>
  );
}
